import { useQuery } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import type { MeResponse } from "@shared/types";
import { ME_QUERY_KEY } from "@/hooks/use-me";
import { currentCorrelationId } from "../correlation";
import { logger } from "../logger";
import { useAuth } from "./AuthContext";
import {
  autoSignInBlock,
  clearAutoSignIn,
  clearSignInReturns,
  consecutiveSignInReturns,
  recordSignInReturn,
  markAutoSignInAttempt,
  type SignInNotice,
} from "./autoSignIn";
import { ApiClientError } from "../api";
import {
  forgetRememberedProvider,
  readRememberedProvider,
  readTabProviderChoice,
  rememberSignedInProvider,
} from "./providerChoice";
import { idleLocked } from "./sessionSync";
import {
  isMsalError,
  signInErrorCode,
  signInErrorView,
  type SignInErrorView,
} from "./signInErrors";
import type { AuthProviderOption } from "./types";

/**
 * Deciding, on every render of a signed-out app, whether to leave for the
 * identity provider (FR-10).
 *
 * The markers and the reasoning behind them are in `./autoSignIn`; this is the
 * React half, kept in its own module so the marker helpers stay importable from
 * `AuthContext` without the two files importing each other.
 */

export type AutoSignIn =
  /**
   * The browser is leaving for the identity provider; show a loader.
   * `expired` says the idle timeout ended the session, so the loader can say why.
   */
  | { phase: "redirecting"; expired?: boolean }
  /** Show the sign-in page, with `notice` when the user did not ask for it. */
  | { phase: "fallback"; notice?: SignInNotice };

/**
 * `/sign-in` and `/callback` never start a redirect, whatever the address's case or a trailing
 * slash (#1644): `/sign-in` is the guaranteed way back to the choice of providers.
 */
function isFallbackRoute(location: string): boolean {
  // Trailing slashes trimmed by hand: a `/\/+$/` pattern backtracks on a long run (Sonar S5852).
  let end = location.length;
  while (end > 0 && location[end - 1] === "/") end -= 1;
  const path = location.slice(0, end).toLowerCase();
  return path === "/sign-in" || path === "/callback";
}

/**
 * The provider an unauthenticated visit may go straight back to (#1644): one that produced a
 * session in this browser before, that the server still lists, and that this tab has not since
 * left for another provider. Null otherwise, and then the sign-in page offers the choice.
 */
function provenProvider(listed: readonly AuthProviderOption[]): string | null {
  const remembered = readRememberedProvider();
  if (remembered === null || !listed.some((provider) => provider.id === remembered)) return null;
  const tab = readTabProviderChoice();
  return tab === null || tab === remembered ? remembered : null;
}

/** An MSAL refusal is logged by its code only (#2006): Microsoft's text names the directory. */
function logRedirectFailure(cause: unknown): void {
  if (isMsalError(cause)) {
    logger.error("sign-in redirect could not be started", undefined, {
      "skydata.reason": signInErrorCode(cause) ?? "unknown",
    });
  } else {
    logger.error("sign-in redirect could not be started", cause);
  }
}

/**
 * The notice for an attempt that came back without a session (#2291). The first return is most
 * often the person pressing Back on the provider's page, so it says only what happened and what to
 * do. Browser site data is named only on a second return in a row, where storage is the likelier
 * cause. The title already says "did not finish", so the body never repeats it.
 */
function returnNotice(returnCount: number): AutoSignIn {
  return {
    phase: "fallback",
    notice: {
      title: "Sign-in did not finish",
      message:
        returnCount >= 2
          ? "This happened again. Sign in again to continue. If it keeps happening, check that this browser allows site data for skyData."
          : "Sign in again to continue.",
      correlationId: currentCorrelationId(),
    },
  };
}

/** A signed-out MSAL visit with nothing already said about it (no refusal, no error). */
function isSignedOutMsal(
  status: string,
  kind: string | undefined,
  failure: SignInErrorView | null,
  error: unknown,
): boolean {
  return kind === "msal" && status === "signed-out" && !failure && !error;
}

/**
 * What a signed-in visit settles once `/me` has answered or refused.
 *
 * B-07: not at "signed-in" (MSAL holds an account) but once `/me` answers. A person the server
 * refuses is signed in at the identity provider and nowhere else; clearing the attempt marker
 * before `/me` sent them straight back to the identity provider for ever.
 * #1644: remembered only once skyData accepts the person through it, forgotten when it refuses
 * them, so the next visit offers the choice instead of the provider that failed.
 */
function settleSignedIn(answered: boolean, accepted: boolean, refused: boolean): void {
  if (answered) {
    clearAutoSignIn();
    clearSignInReturns();
  }
  if (accepted) rememberSignedInProvider();
  else if (refused) {
    forgetRememberedProvider();
    // The provider did produce a session (skyData refused it): not a return without one.
    clearSignInReturns();
  }
}

/**
 * Starts the redirect when it is the right thing to do, and says what to render
 * while it does not.
 *
 * The dev token provider is deliberately untouched: there is no identity
 * provider to redirect to, the persona buttons ARE the sign-in, and the
 * walkthrough and the Playwright suite drive them (LOCAL-DEV §3).
 */
export function useAutoSignIn(location: string): AutoSignIn {
  const { status, kind, error, signIn, providers } = useAuth();
  // Issue #2006: the redirect MSAL refused to start, in `signInErrorView`'s words.
  const [failure, setFailure] = useState<SignInErrorView | null>(null);
  const started = useRef(false);
  // #2291: the attempt whose return was counted, and the count it made.
  const countedReturn = useRef<{ at: number; count: number } | null>(null);
  const [returns, setReturns] = useState<number | null>(null);
  // B-07: the round trip is over only when skyData itself accepts the person. This observer never
  // fetches (the shell's `useMe` does); it only hears the answer.
  const me = useQuery<MeResponse>({ queryKey: ME_QUERY_KEY, enabled: false });
  const meAnswered = me.isSuccess;
  // #1644: the person is in only when `/me` answers with no step still to give.
  const meAccepted = me.isSuccess && !me.data?.signInStep;
  const meRefused =
    me.error instanceof ApiClientError && (me.error.status === 401 || me.error.status === 403);

  const onFallbackRoute = isFallbackRoute(location);
  const block = autoSignInBlock();
  // Round 9 review, owner: one click, not zero. After an idle logoff (the lock, in every tab and
  // every later document) nothing leaves for the identity provider by itself; the sign-in page
  // asks for the one click, which picks the account (`selectAccount`, the address as the hint).
  const locked = idleLocked();
  // Workforce SSO: with more than one way to sign in there is a choice to make, so an ordinary
  // visit shows the sign-in page instead of picking for the person, unless a provider produced a
  // session in this browser before (round 9, sign-out fix D; #1644): then it goes straight back to
  // that provider. A provider only chosen, whose round trip never came back with a session, is no
  // reason to go anywhere: the remembered provider is forgotten when a redirect leaves and written
  // again only once `/me` accepts the person (`providerChoice.ts`), so the app never sends a person
  // to a provider twice without a session in between. Never after an idle logoff: that waits for
  // one click (the lock, below).
  const listed = providers ?? [];
  const choice = listed.length > 1 && provenProvider(listed) === null;
  const eligible =
    isSignedOutMsal(status, kind, failure, error) &&
    !locked &&
    !choice &&
    !onFallbackRoute &&
    block === null;

  const showsReturn = block?.reason === "attempt" && isSignedOutMsal(status, kind, failure, error);
  const returnAt = block?.at ?? null;
  useEffect(() => {
    if (!showsReturn || returnAt === null) return;
    if (countedReturn.current?.at === returnAt) return;
    const count = recordSignInReturn();
    countedReturn.current = { at: returnAt, count };
    setReturns(count);
  }, [showsReturn, returnAt]);

  useEffect(() => {
    if (status === "signed-in") {
      started.current = false;
      settleSignedIn(meAnswered, meAccepted, meRefused);
      return;
    }
    if (!eligible || started.current) return;
    started.current = true;
    markAutoSignInAttempt();
    logger.info("starting sign-in redirect");
    void signIn(undefined).catch((cause: unknown) => {
      logRedirectFailure(cause);
      setFailure(signInErrorView(cause));
    });
  }, [eligible, signIn, status, meAnswered, meAccepted, meRefused]);

  if (eligible) return { phase: "redirecting" };

  // The sign-in configuration or MSAL could not be loaded (Session 9 contract
  // §2.5). There is nowhere to redirect to, and the page's own "Sign-in is
  // unavailable" message with Retry says why; an older attempt marker must not
  // dress it up as a round trip that did not finish.
  // #2006: so is a refusal the identity provider sent back, which the page says in its own words
  // (`signInErrors.ts`); the attempt marker's "did not finish" notice would repeat it.
  if (status === "failed" || error) return { phase: "fallback" };

  if (failure) {
    return {
      phase: "fallback",
      notice: {
        title: "We could not send you to sign in",
        message: failure.message,
        ...(failure.code ? { code: failure.code } : {}),
        correlationId: currentCorrelationId(),
      },
    };
  }

  // An attempt that came back without a session (#2291). The first return is most often the
  // person pressing Back on the provider's page, so it says only what happened and what to do.
  // Browser site data is named only on a second return in a row, where storage is the likelier
  // cause (a provider's refusal is said in its own words above, and an MSAL storage error by
  // `signInErrors.ts`).
  if (block?.reason === "attempt" && status !== "signed-in" && kind === "msal") {
    return returnNotice(returns ?? consecutiveSignInReturns() + 1);
  }

  // An idle logoff, here or in another tab (B-18), or a document opened while locked: say why.
  if ((block?.reason === "expired" || locked) && status !== "signed-in") {
    return {
      phase: "fallback",
      notice: {
        title: "Your session ended",
        message:
          "skyData signed you out after a period of inactivity in this browser. Sign in again to continue.",
        correlationId: currentCorrelationId(),
      },
    };
  }

  return { phase: "fallback" };
}
